import { useQueryClient } from "@tanstack/react-query";
import {
  useDeleteWithUndoController,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import {
  type CSSProperties,
  type MouseEvent,
  useEffect,
  useState,
} from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import type { Task as TData } from "../types";
import {
  postponedToNextMonday,
  postponedToNextWeek,
  postponedToTomorrow,
} from "./taskPostpone";
import { isDueToday, isDueTomorrow, isOverdue } from "./tasksPredicate";
import { TaskAccountFields } from "./TaskAccountFields";
import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";
import { TaskOwnerAvatar } from "./TaskOwnerAvatar";
import { TaskRowActions } from "./TaskRowActions";
import { TaskCadence, TaskDueCell, TaskTypeIcon } from "./TaskRowFields";
import { useTaskSelection } from "./TaskSelectionContext";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * The columns a row is laid out on once it has the width for them, in order:
 * select · done · type · text · account · due · cadence · actions.
 *
 * Every row is its own grid, so only widths that do not depend on the row's own
 * content put fifty due dates on one vertical rail: an `auto` column would be
 * as wide as that row happens to need and the rail would zigzag. Due date and
 * cadence are therefore fixed, while text and account share what is left in a
 * fixed ratio -- so a long account name is truncated instead of pushing the due
 * date sideways, and neither of them can collapse to nothing in a narrow list.
 */
const buildRowColumns = ({
  hasSelection,
  showContact,
}: {
  hasSelection: boolean;
  showContact: boolean;
}) =>
  [
    hasSelection ? "auto" : null,
    "auto",
    "auto",
    "minmax(0,1.6fr)",
    showContact ? "minmax(0,1fr)" : null,
    "7rem",
    "6.5rem",
    "auto",
  ]
    .filter(Boolean)
    .join(" ");

export const Task = ({
  task,
  showContact,
  showOwner,
  hideDueDateWhenSingleDay,
}: {
  task: TData;
  showContact?: boolean;
  /** Show the owning account manager. Used on cross-account-manager lists. */
  showOwner?: boolean;
  /**
   * Drop the date (keeping the time) for tasks due today or tomorrow. Only set
   * this from a list whose heading already names the date — a flat list has
   * nothing else to say which day a task falls on.
   */
  hideDueDateWhenSingleDay?: boolean;
}) => {
  const isMobile = useIsMobile();
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const selection = useTaskSelection();

  const [openEdit, setOpenEdit] = useState(false);

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const [update, { isPending: isUpdatePending, isSuccess, variables }] =
    useUpdate();
  const { handleDelete } = useDeleteWithUndoController({
    record: task,
    redirect: false,
    mutationOptions: {
      onSuccess() {
        notify("resources.tasks.deleted", {
          undoable: true,
        });
      },
    },
  });

  const handleEdit = () => {
    setOpenEdit(true);
  };

  const handleCheck = () => () => {
    update("tasks", {
      id: task.id,
      data: {
        done_date: task.done_date ? null : new Date().toISOString(),
      },
      previousData: task,
    });
  };

  /**
   * Postponing moves the task's own due date, it does not overwrite it with a
   * date built from "now": a call planned for 09:00 stays a 09:00 call. Writing
   * a date-only string here used to drop the time to midnight UTC, which in any
   * timezone behind UTC landed the task on the day *before* the one the menu
   * item promised — and put it straight back in the overdue bucket.
   */
  const handlePostpone = (nextDueDate: Date) => () => {
    update("tasks", {
      id: task.id,
      data: { due_date: nextDueDate.toISOString() },
      previousData: task,
    });
  };

  useEffect(() => {
    // We do not want to invalidate the query when a tack is checked or unchecked
    if (
      isUpdatePending ||
      !isSuccess ||
      variables?.data?.done_date != undefined
    ) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["tasks", "getList"] });
  }, [queryClient, isUpdatePending, isSuccess, variables]);

  // The Today and Tomorrow headings already name the date, so repeating it on
  // every row is noise. Buckets spanning several days keep it, and so does any
  // list that is not grouped by due date at all.
  const isSingleDayBucket =
    hideDueDateWhenSingleDay &&
    (isDueToday(task.due_date) || isDueTomorrow(task.due_date));

  const isTaskDone = task.done_date != null;
  const isSelected = selection?.selectedIds.includes(task.id) ?? false;
  // The row-level mark for a late task. The bucket heading alone cannot carry
  // it: two rows past the heading it is off the screen, and a task three weeks
  // late then renders exactly like one due in five weeks.
  const isTaskOverdue = !isTaskDone && isOverdue(task.due_date);

  // The row body opens the task; the controls inside it do their own job.
  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  return (
    <>
      <div
        style={
          {
            "--task-row-columns": buildRowColumns({
              hasSelection: selection != null,
              showContact: !!showContact,
            }),
          } as CSSProperties
        }
        className={cn(
          "group relative flex cursor-pointer items-start gap-2 border-l-2 py-2.5 pr-1 pl-2 transition-colors",
          // Laid out against the WIDTH OF THE LIST, not of the window: the same
          // row is rendered in the full-width tasks page and in a 300px contact
          // aside, and a viewport breakpoint cannot tell those apart.
          "@2xl:grid @2xl:items-baseline @2xl:gap-x-3 @2xl:py-1.5 @2xl:[grid-template-columns:var(--task-row-columns)]",
          isTaskOverdue ? "border-l-destructive" : "border-l-transparent",
          // A selected row is tinted, so the box that tints it cannot be
          // mistaken for the one that completes the task.
          isSelected ? "bg-primary/10" : "hover:bg-muted",
        )}
        onClick={handleEdit}
      >
        {/*
          Selecting and completing are two different verbs, so they are two
          different controls: the square selection box sits apart in its own
          gutter and tints the whole row when it is on, while the circle that
          completes the task sits against the text it completes.
        */}
        {selection && (
          <span
            className="flex shrink-0 items-center self-start pt-0.5 pr-2 @2xl:self-center @2xl:pt-0 @2xl:pr-3"
            onClick={stopPropagation}
          >
            <Checkbox
              className="cursor-pointer"
              checked={isSelected}
              onCheckedChange={() => selection.onToggleItem(task.id)}
              aria-label={translate("ra.action.select_row", { _: "Select" })}
            />
          </span>
        )}

        <span
          className="-m-2 flex shrink-0 items-center self-start p-2 @2xl:self-center md:m-0 md:p-0"
          onClick={stopPropagation}
        >
          <Checkbox
            checked={isTaskDone}
            onCheckedChange={handleCheck()}
            disabled={isUpdatePending}
            className="size-4.5 cursor-pointer rounded-full"
            aria-label={translate(
              isTaskDone
                ? "resources.tasks.actions.mark_not_done"
                : "resources.tasks.actions.mark_done",
              { _: isTaskDone ? "Mark not done" : "Mark done" },
            )}
          />
        </span>

        <TaskTypeIcon
          type={task.type}
          className="self-start @2xl:self-center"
        />

        {/*
          Two stacked lines in a narrow list; once there is room, `contents`
          dissolves these wrappers so text, account, due date and cadence
          become cells of the row's own grid.
        */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 @2xl:contents">
          <button
            type="button"
            title={task.text}
            onClick={(event) => {
              stopPropagation(event);
              handleEdit();
            }}
            className={cn(
              "min-w-0 cursor-pointer rounded-xs text-left text-sm underline-offset-2 line-clamp-2 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none @2xl:line-clamp-1",
              isTaskDone && "text-muted-foreground line-through",
            )}
          >
            {task.text}
          </button>

          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground @2xl:contents">
            {showContact && (
              <span
                onClick={stopPropagation}
                // TaskAccountFields ends every field with a separator, which
                // used to be followed by the due date on the same line. Now
                // that the due date is a column of its own, the last one would
                // dangle.
                className="min-w-0 truncate [&>span:last-child]:hidden"
              >
                <TaskAccountFields task={task} />
              </span>
            )}
            <TaskDueCell task={task} timeOnly={isSingleDayBucket} />
            <TaskCadence task={task} />
          </div>
        </div>

        <div
          className="ml-auto flex shrink-0 items-center gap-0.5 self-start @2xl:ml-0 @2xl:justify-end @2xl:self-center"
          onClick={stopPropagation}
        >
          {showOwner && <TaskOwnerAvatar task={task} />}
          <TaskRowActions
            onPostponeTomorrow={handlePostpone(postponedToTomorrow(task))}
            onPostponeNextWeek={handlePostpone(postponedToNextWeek(task))}
            onPostponeNextMonday={handlePostpone(postponedToNextMonday(task))}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {isMobile ? (
        <TaskEditSheet
          taskId={task.id}
          open={openEdit}
          onOpenChange={setOpenEdit}
        />
      ) : (
        <TaskEdit taskId={task.id} open={openEdit} close={handleCloseEdit} />
      )}
    </>
  );
};
